import { mapApprovalPool } from './mapApprovalPool'
import { mapApprover } from './mapApprover'
import { type ApprovalPoolConstructorData } from '../../entities/internal/ApprovalPool'
import { type ApprovalWorkflowDetailsConstructorData } from '../../entities/internal/ApprovalWorkflowDetails/ApprovalWorkflowDetails'
import { type ApproverConstructor } from '../../entities/internal/Approver'
import { type ApprovalStepStatusEnum } from '../../entities/internal/enums/ApprovalStepStatusEnum'
import { type ApprovalWorkflowDetailsApiModel } from '../api-models/ApprovalWorkflowDetailsApiModel'
import { type ApproverApiModel } from '../api-models/ApproverApiModel'

// Some historical data can be missing some data such as group info. In these cases, the BE fills the fields with placeholder values
// and assigns it an id of 0.
const UNKNOWN_GROUP_ID = 0

const mapStepData = (step: {
  status: ApprovalStepStatusEnum
  group: {
    id: number
    name: string
  }
  step_number: number
  level?: number
  is_pooled: boolean
  available_approvers: ApproverApiModel[]
  next_approvers: Nullable<ApproverApiModel[]>
  reviewed_by: Nullable<
    {
      user_id: number
      reviewed_at: ISODateString
      on_behalf_of: Nullable<number>
    }[]
  >
}) => {
  const approvers: ApproverConstructor[] = step.available_approvers.map(
    (approver) => mapApprover(approver, step.group.name)
  )

  let selectedApprover: ApproverConstructor | ApprovalPoolConstructorData | null
  if (!step.next_approvers || step.next_approvers.length === 0) {
    // No approvers selected
    selectedApprover = null
  } else if (
    step.is_pooled &&
    step.next_approvers.length > 1 &&
    step.level !== undefined
  ) {
    // Approval pool selected
    selectedApprover = mapApprovalPool({
      approvers: step.next_approvers,
      group_id: step.group.id,
      group_name: step.group.name,
      level: step.level,
    })
  } else {
    // Single approver selected
    selectedApprover = mapApprover(step.next_approvers[0], step.group.name)
  }

  // With the way the system currently works, a step can only have one reviewer. If there are items in
  // the reviewed_by array, use the first one.
  const reviewedBy =
    step.reviewed_by && step.reviewed_by.length > 0
      ? {
          id: step.reviewed_by[0].user_id,
          onBehalfOf: step.reviewed_by[0].on_behalf_of,
          reviewedAt: step.reviewed_by[0].reviewed_at,
        }
      : null

  // If we're dealing with an "unknown" group, we want to set the groupId and groupName to null
  // rather than using the values the BE fills in as they mean nothing to the user.
  const isUnknownGroup = step.group.id === UNKNOWN_GROUP_ID
  const groupId = isUnknownGroup ? null : step.group.id
  const groupName = isUnknownGroup ? null : step.group.name

  return {
    status: step.status,
    stepNumber: step.step_number,
    groupId,
    groupName,
    level: step.level ?? null,
    approvers,
    selectedApprover,
    reviewedBy,
  }
}

export const mapApprovalWorkflowDetailsApiModel = ({
  is_complete,
  steps,
}: ApprovalWorkflowDetailsApiModel): ApprovalWorkflowDetailsConstructorData => {
  return {
    isComplete: is_complete,
    steps: steps.map(mapStepData),
  }
}
